import type { BadgeTone } from '@provenance/design';
import { Badge } from '@provenance/design';
import { DocumentStatus, type DocumentStatus as Status } from '@provenance/shared';

/**
 * Status presentation, defined in exactly one place.
 *
 * Status colour is meaning, not decoration — an operator scanning a queue reads the
 * colour before the word. Defining the mapping once means a status cannot look like one
 * thing on the queue and another on the detail screen.
 */
const DOCUMENT_STATUS: Record<Status, { label: string; tone: BadgeTone }> = {
  [DocumentStatus.AWAITING_ACTION]: { label: 'Awaiting action', tone: 'warn' },
  [DocumentStatus.CLARIFICATION_REQUESTED]: { label: 'Awaiting clarification', tone: 'clay' },
  [DocumentStatus.APPROVED]: { label: 'Issued', tone: 'ok' },
  [DocumentStatus.REJECTED]: { label: 'Rejected', tone: 'stop' },
  [DocumentStatus.REVOKED]: { label: 'Revoked', tone: 'stop' },
};

export function DocumentStatusBadge({ status, size = 'md' }: { status: Status; size?: 'sm' | 'md' }) {
  const config = DOCUMENT_STATUS[status] ?? { label: status, tone: 'neutral' as BadgeTone };
  return (
    <Badge tone={config.tone} dot size={size}>
      {config.label}
    </Badge>
  );
}

export function documentStatusLabel(status: Status): string {
  return DOCUMENT_STATUS[status]?.label ?? status;
}

const REGISTRATION_STATUS: Record<string, { label: string; tone: BadgeTone }> = {
  SUBMITTED: { label: 'Submitted', tone: 'warn' },
  INFO_REQUESTED: { label: 'Information requested', tone: 'clay' },
  INFO_PROVIDED: { label: 'Response received', tone: 'info' },
  APPROVED: { label: 'Approved', tone: 'ok' },
  REJECTED: { label: 'Rejected', tone: 'stop' },
};

export function RegistrationStatusBadge({ status }: { status: string }) {
  const config = REGISTRATION_STATUS[status] ?? { label: status, tone: 'neutral' as BadgeTone };
  return (
    <Badge tone={config.tone} dot size="sm">
      {config.label}
    </Badge>
  );
}

const CHECK_TONE: Record<string, BadgeTone> = {
  PASS: 'ok',
  WARNING: 'warn',
  FAIL: 'stop',
  UNAVAILABLE: 'neutral',
};

export function checkTone(status: string): BadgeTone {
  return CHECK_TONE[status] ?? 'neutral';
}

const INTEGRATOR_STATUS: Record<string, { label: string; tone: BadgeTone }> = {
  ACTIVE: { label: 'Active', tone: 'ok' },
  SUSPENDED: { label: 'Suspended', tone: 'stop' },
  ARCHIVED: { label: 'Archived', tone: 'neutral' },
};

export function IntegratorStatusBadge({ status }: { status: string }) {
  const config = INTEGRATOR_STATUS[status] ?? { label: status, tone: 'neutral' as BadgeTone };
  return (
    <Badge tone={config.tone} dot size="sm">
      {config.label}
    </Badge>
  );
}

/** API key lifecycle state, as derived by the server (see IntegratorsService.detail). */
const KEY_STATE: Record<string, { label: string; tone: BadgeTone }> = {
  ACTIVE: { label: 'Active', tone: 'ok' },
  EXPIRING: { label: 'Expires', tone: 'warn' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
  REVOKED: { label: 'Revoked', tone: 'stop' },
};

export function ApiKeyStateBadge({ state }: { state: string }) {
  const config = KEY_STATE[state] ?? { label: state, tone: 'neutral' as BadgeTone };
  return (
    <Badge tone={config.tone} size="sm" dot={state === 'ACTIVE' || state === 'REVOKED'}>
      {config.label}
    </Badge>
  );
}

const DELIVERY_STATUS: Record<string, { label: string; tone: BadgeTone }> = {
  PENDING: { label: 'Pending', tone: 'neutral' },
  SUCCEEDED: { label: 'Delivered', tone: 'ok' },
  FAILED: { label: 'Retrying', tone: 'warn' },
  DEAD_LETTERED: { label: 'Dead-lettered', tone: 'stop' },
};

export function DeliveryStatusBadge({ status }: { status: string }) {
  const config = DELIVERY_STATUS[status] ?? { label: status, tone: 'neutral' as BadgeTone };
  return (
    <Badge tone={config.tone} size="sm">
      {config.label}
    </Badge>
  );
}
